/**
 * Vista ampliada de una galería.
 *
 * LO QUE LA HACE USABLE NO ES LA IMAGEN GRANDE, SON LOS ATAJOS: ← y → para pasar, Esc para
 * salir. Una vista ampliada que obliga a apuntar con el ratón a una flechita de 24 píxeles
 * para ver la siguiente foto es más incómoda que la miniatura.
 *
 * Detalles que no se ven pero se notan:
 *
 * · **El foco entra y vuelve.** Al abrir, el foco va al diálogo; al cerrar, vuelve al elemento
 *   que lo abrió. Sin lo segundo, quien navega con teclado aterriza al principio de la página
 *   y tiene que recorrerla otra vez hasta donde estaba.
 * · **El fondo no se desplaza.** Se bloquea el scroll del documento mientras está abierta: si
 *   no, la rueda del ratón mueve la página de debajo y al cerrar no reconoces dónde estás.
 * · **Precarga de la vecina.** Al mostrar una foto se pide también la siguiente, de modo que
 *   pasar a la derecha —que es lo que hace todo el mundo— sea instantáneo.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../Icon';
import styles from './Lightbox.module.css';

export interface LightboxImage {
  id: string;
  /** `null` cuando el objeto no está en el almacén: se pinta el marcador, no un icono roto. */
  url: string | null;
  caption?: string | null;
  /** Color medio, para pintar algo mientras carga la imagen real. */
  placeholderColor?: string | null;
}

interface LightboxProps {
  images: LightboxImage[];
  startIndex: number;
  onClose: () => void;
}

export function Lightbox({ images, startIndex, onClose }: LightboxProps) {
  const [index, setIndex] = useState(() => Math.min(Math.max(startIndex, 0), images.length - 1));
  const dialogRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<Element | null>(null);

  const go = useCallback(
    (delta: number) => {
      setIndex((current) => (current + delta + images.length) % images.length);
    },
    [images.length],
  );

  useEffect(() => {
    openerRef.current = document.activeElement;
    dialogRef.current?.focus();

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
      // El foco vuelve a donde estaba, si ese elemento sigue en el documento.
      const opener = openerRef.current;
      if (opener instanceof HTMLElement && document.contains(opener)) opener.focus();
    };
  }, []);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        go(1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        go(-1);
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [go, onClose]);

  // Precarga de la siguiente. El navegador la cachea y el salto se nota instantáneo.
  useEffect(() => {
    const next = images[(index + 1) % images.length];
    if (!next?.url) return;
    const image = new Image();
    image.src = next.url;
  }, [index, images]);

  const current = images[index];
  if (!current) return null;

  return createPortal(
    <div
      className={styles.scrim}
      role="dialog"
      aria-modal="true"
      aria-label="Vista ampliada de la foto"
      ref={dialogRef}
      tabIndex={-1}
      // Pinchar FUERA cierra; pinchar en la imagen, no. Es lo que la gente espera.
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <button type="button" className={styles.close} onClick={onClose} aria-label="Cerrar">
        <Icon name="close" size={20} strokeWidth={2.2} />
      </button>

      {images.length > 1 && (
        <button
          type="button"
          className={`${styles.nav} ${styles.prev}`}
          onClick={() => go(-1)}
          aria-label="Foto anterior"
        >
          <Icon name="chevron-left" size={26} strokeWidth={2.2} />
        </button>
      )}

      <figure className={styles.figure}>
        {current.url ? (
          <img
            key={current.id}
            className={styles.image}
            src={current.url}
            alt={current.caption || 'Foto del inmueble'}
            style={
              current.placeholderColor ? { backgroundColor: current.placeholderColor } : undefined
            }
          />
        ) : (
          <div className={styles.missing}>Esta foto no tiene fichero en el almacén.</div>
        )}
        <figcaption className={styles.caption}>
          <span>{current.caption || 'Sin pie'}</span>
          <span className={styles.counter} aria-live="polite">
            {index + 1} / {images.length}
          </span>
        </figcaption>
      </figure>

      {images.length > 1 && (
        <button
          type="button"
          className={`${styles.nav} ${styles.next}`}
          onClick={() => go(1)}
          aria-label="Foto siguiente"
        >
          <Icon name="chevron-right" size={26} strokeWidth={2.2} />
        </button>
      )}

      <p className={styles.hint}>← → para pasar · Esc para cerrar</p>
    </div>,
    document.body,
  );
}
